// Solution sharing switches in the role editor (solution sharing plan, stage 6). LEAF module: pure,
// unit-tested (src/utils/__tests__/sharingSwitches.test.js).
//
// Two base permissions stored in the role's "Solution Management" row, each Off (0) / Partial (1,
// "can use") / Full (2, "can use and switch it on, at Partial, for roles it manages"):
//   "Share Solutions"          - people in the role may share the solutions they created;
//   "Receive Shared Solutions" - the role accepts shared solutions.
// The editor shows them as two cells in the Solution Management row, right after its own tabs
// (owner decision S6-1 B) but keeps their values OUT of the Base Permissions matrix state (they are not in the
// catalogue, so the matrix loader drops them, and the grant-all expansion never grants them) and
// merges them back on save. That keeps them clear, by construction, of the matrix rules they must
// not follow: the caller clamps, hiding tabs the editor does not hold, the row's Overall control,
// the parent-permission cap, the grant-all display fold and the inert table on a grant-all role.
// The server applies the same rules (rolesMS sharingSwitches.js).
import { rank as permRank } from "./permissionDependencies";

export const SWITCH_MODULE = "Solution Management";
export const SHARE = "Share Solutions";
export const RECEIVE = "Receive Shared Solutions";
export const SWITCHES = [SHARE, RECEIVE];
const permOf = (r) => (r >= 2 ? "ALL" : r >= 1 ? "READ" : "disable");

export const isSwitchTab = (moduleName, tabName) =>
  moduleName === SWITCH_MODULE && SWITCHES.indexOf(tabName) !== -1;

// The named level of a switch: the literally named tab in the role's Solution Management row(s),
// the highest when rows repeat. A '*' row never counts.
export function switchLevelOf(modules, tab) {
  let r = 0;
  (modules || []).forEach((m) => {
    if (!m || m.name !== SWITCH_MODULE) return;
    (m.tabs || []).forEach((t) => {
      if (t && t.name === tab) r = Math.max(r, permRank(t.permission));
    });
  });
  return r;
}

export function loadSwitches(roleDoc) {
  const modules = (roleDoc && roleDoc.modules) || [];
  return { [SHARE]: switchLevelOf(modules, SHARE), [RECEIVE]: switchLevelOf(modules, RECEIVE) };
}

// The highest level this editor may switch a switch ON to (O8.1 e): a grant-all editor any level,
// an editor holding it at Full only Partial, anyone else nothing; nothing at all while sharing is
// off for the tenant (the server refuses every rise then).
export function maxRaise({ tab, editorModules, editorGrantAll, sharingOn }) {
  if (!sharingOn) return 0;
  if (editorGrantAll) return 2;
  return switchLevelOf(editorModules, tab) >= 2 ? 1 : 0;
}

// The levels the editor offers for one switch: keeping, lowering and switching off are always
// allowed; raising only up to maxRaise. `stored` is the level the role is stored with (the editor's
// starting point): the server judges rises against it, so going back to it after lowering is not a
// raise and stays offered. `current` is what is on screen.
export function switchOptions({ current, stored, tab, editorModules, editorGrantAll, sharingOn }) {
  const ceiling = Math.max(current || 0, stored || 0, maxRaise({ tab, editorModules, editorGrantAll, sharingOn }));
  return [0, 1, 2].filter((l) => l <= ceiling);
}

// Clone is a create: each switch starts at most at what the editor may switch on (otherwise Off).
export function cloneSeed(source, ctx) {
  const out = {};
  SWITCHES.forEach((tab) => {
    out[tab] = Math.min((source && source[tab]) || 0, maxRaise({ ...ctx, tab }));
  });
  return out;
}

// Put the switches into a serialized modules[] (the PUT/POST body): onto the existing Solution
// Management row, or a new row when the matrix wrote none (a switch-only row must survive).
// Switches at Off are left out. Pure: returns a new array.
export function mergeSwitches(modules, switches) {
  const tabs = SWITCHES.filter((tab) => switches && switches[tab] >= 1).map((tab) => ({
    name: tab,
    permission: permOf(switches[tab]),
  }));
  const out = (modules || []).map((m) =>
    m && m.name === SWITCH_MODULE
      ? { ...m, tabs: (m.tabs || []).filter((t) => !(t && SWITCHES.indexOf(t.name) !== -1)) }
      : m
  );
  if (!tabs.length) return out;
  const row = out.find((m) => m && m.name === SWITCH_MODULE);
  if (row) row.tabs = [...row.tabs, ...tabs];
  else out.push({ name: SWITCH_MODULE, tabs });
  return out;
}

// ---- O23 (owner 2026-10-21): Share Solutions needs Solution Creation at Full on the same role -------
// The server refuses a role that breaks it (rolesMS shareCreation.js); the editor greys Share's raises
// and switches Share off whenever the role stops meeting it. A '*' tab inside the row does not count.
export const CREATION_TAB = "Solution Creation";
export const SHARE_NEEDS_CREATION_HINT = "Needs Solution Creation at Full Access on this role.";
export const SHARE_SWITCHED_OFF_NOTE = `"${SHARE}" was switched off: it needs "${CREATION_TAB}" at Full Access.`;
export const SHARE_VIEW_ONLY_NOTE = `"${SHARE}" needs "${CREATION_TAB}" at Full Access; saving this role would switch it off.`;

// The editor's state (`utils` rows plus the '*' grant ranks `anchorGrant`) meets O23: Solution
// Creation at Full in the Solution Management row, or the role's '*' base grant at Full.
export function meetsShareCreation(utils, anchorGrant) {
  return ((anchorGrant && anchorGrant.base) || 0) >= 2 || switchLevelOf(utils, CREATION_TAB) >= 2;
}

// Would this state switch Share off (Share Partial or Full while O23 is not met)?
export function shareSwitchesOff(switches, utils, anchorGrant) {
  return ((switches && switches[SHARE]) || 0) >= 1 && !meetsShareCreation(utils, anchorGrant);
}

// ---- Stage 7: sharing a solution ----------------------------------------------------------------

// Offer "Share" (the solution menu, the wizard's last step) only while sharing is on for the tenant,
// to a grant-all admin, or to the solution's creator whose role holds Share at Partial or Full and
// Solution Creation at Full (O23; `ownModules` is metaData.apps, where '*' rows are already expanded)
// and whose own entry for it is not group-limited. The server applies the full gates (4d) anyway.
export function canOfferShare({ sharingOn, grantAll, ownModules, isCreator, groupLimited }) {
  if (!sharingOn || groupLimited) return false;
  if (grantAll) return true;
  return !!isCreator && switchLevelOf(ownModules, SHARE) >= 1 && switchLevelOf(ownModules, CREATION_TAB) >= 2;
}

const idOf = (x) => String(x && x._id !== undefined ? x._id : x);
const byName = (a, b) => String(a.name || "").localeCompare(String(b.name || ""));

// The confirmation for the chosen targets (share-targets rows): every role that will receive the
// solution in two groups (the chosen ones, then their administrator roles - a role both chosen and an
// administrator of another chosen role is listed once, as chosen), the users-visibility lines
// (O11: chosen roles left out; O12: per receiver), and the `confirmed` lists the POST must carry
// (those share-targets returned for each chosen role, before merging).
export function shareConfirmation(targets, chosenIds) {
  const chosenSet = new Set((chosenIds || []).map(String));
  const chosen = (targets || []).filter((t) => chosenSet.has(idOf(t)));
  const admins = new Map();
  const hidden = new Map();
  const shown = new Map(); // receiver id -> { receiver, roles: Map }
  for (const t of chosen) {
    for (const a of t.alsoTo || []) if (!chosenSet.has(idOf(a))) admins.set(idOf(a), a);
    for (const v of t.usersHiddenFrom || []) if (!chosenSet.has(idOf(v))) hidden.set(idOf(v), v);
    for (const e of t.usersShownTo || []) {
      const k = idOf(e.receiver);
      if (!shown.has(k)) shown.set(k, { receiver: e.receiver, roles: new Map() });
      for (const y of e.roles || []) shown.get(k).roles.set(idOf(y), y);
    }
  }
  const confirmed = {};
  for (const t of chosen)
    confirmed[idOf(t)] = { alsoTo: t.alsoTo || [], usersHiddenFrom: t.usersHiddenFrom || [], usersShownTo: t.usersShownTo || [] };
  return {
    chosen: chosen.map((t) => ({ _id: t._id, name: t.name })).sort(byName),
    administrators: [...admins.values()].sort(byName),
    // O11: these roles would stop seeing the users of the chosen roles that contain them
    usersHiddenFrom: [...hidden.values()].sort(byName),
    usersHiddenOf: chosen.filter((t) => (t.usersHiddenFrom || []).some((v) => !chosenSet.has(idOf(v)))).map((t) => ({ _id: t._id, name: t.name })),
    usersShownTo: [...shown.values()].map((e) => ({ receiver: e.receiver, roles: [...e.roles.values()].sort(byName) })),
    confirmed,
  };
}

// Plain-language reason (and remedy) for a role with Receive on that is not offered (grant-all view).
export function notOfferedReason(rule, detail) {
  const d = detail ? ` (${detail})` : "";
  switch (rule) {
    case "would-gain-management":
      return `Sharing would let an administrator role newly manage another role${d}. A grant-all admin can give that administrator the solution in the role editor first.`;
    case "management-capable-peer":
      return `It has an identical role that manages roles or users${d}; sharing would lift one above the other.`;
    case "would-equal-administrator":
      return `It would become identical to one of its administrator roles${d}.`;
    case "administrator-group-limited":
      return `One of its administrator roles holds this solution only for some groups${d}.`;
    case "administrator-no-entry":
      return `One of its administrator roles cannot carry this solution${d} (Threshold Management needs Controlling).`;
    case "administrator-lacks-threshold-profiles":
      return `One of its administrator roles holds this solution without a Threshold Profiles column${d}.`;
    case "star-row-below-full":
      return `A role that holds every solution only at Partial Access contains it${d}.`;
    case "no-feature-tab":
      return "It would get no feature on this solution.";
    case "no-entry":
      return "It cannot carry this solution (Threshold Management needs Controlling).";
    default:
      return "It cannot receive this solution.";
  }
}
